import React from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { clientProofHeaders, clientProofMessage } from '../lib/clientProof';

function loadAppleSDK() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) {
      resolve();
      return;
    }
    const existing = document.getElementById('apple-signin-sdk');
    if (existing) {
      // Script tag exists but AppleID not ready yet — wait for it
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = 'apple-signin-sdk';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function AppleLoginButton() {
  const { API_URL } = useAuth();

  const handleClick = async () => {
    try {
      await loadAppleSDK();
    } catch {
      alert('Failed to load Apple Sign In. Please try again.');
      return;
    }

    try {
      window.AppleID.auth.init({
        clientId: 'com.prozpects.auth',
        scope: 'name email',
        redirectURI: window.location.origin + '/api/auth/apple/callback',
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();
      const idToken = response.authorization?.id_token;
      const user = response.user;

      if (!idToken) {
        alert('No identity token received from Apple.');
        return;
      }

      const res = await axios.post(`${API_URL}/auth/apple/login`, {
        identity_token: idToken,
        first_name: user?.name?.firstName || null,
        last_name: user?.name?.lastName || null,
      }, { headers: await clientProofHeaders(API_URL, 'login') });

      const { access_token } = res.data;
      localStorage.setItem('token', access_token);
      if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      window.location.href = '/dashboard';
    } catch (error) {
      if (error?.error === 'popup_closed_by_user') return;
      if (error?.response?.data?.detail?.code === 'existing_account_use_invite') {
        window.location.href = '/login?error=existing_account_use_invite';
        return;
      }
      console.error('Apple sign-in failed:', error);
      alert(clientProofMessage(error) || 'Apple sign-in failed. Please try again.');
    }
  };

  return (
    <button
      type="button"
      className="apple-signin-btn"
      onClick={handleClick}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
      Continue with Apple
    </button>
  );
}
